import { useState, FormEvent, useEffect, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Select from "react-select";
import ModalComponent from "components/modal";
import { emptyExpenseRow, ExpenseItem } from "../detail";
import ActionButton from "components/table/action-btn";
import { IconMinus } from "@tabler/icons-react";
import { getLocationsByUser } from "api/location";
import { getTemplatesByLocation } from "api/template";
import { LocationType, Template } from "types/dict";
import { useSharedState } from "state";
import { createDocumentRequest, uploadDocumentFiles } from "api/document";

type FormErrors = Record<string, boolean>;
type FormRecords = Record<string, string>;

const REQUIRED_FIELDS = [
  "location_id",
  "template_id",
  "contragent_id",
  "lease_number",
  "contract_start_date",
  "contract_end_date",
  "description",
  "file",
] as const;

const getInputErrorMessage = (fieldId: string) =>
  fieldId === "file" ? "Добавьте договор" : "Введите корректное значение";

interface NewDocumentProps {
  onClose?: () => void;
}

export default function NewDocument({ onClose }: NewDocumentProps = {}) {
  const {
    budgetTypes,
    getBudgetTypesData,
    getDocumentsData,
    getDocumentsTableData,
    contractors,
    getContractorsData,
    copyData,
    setCopyData,
    notify,
  } = useSharedState();
  const { locationName } = useParams();

  const [formRecords, setFormRecords] = useState<FormRecords>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const [fileList, setFileList] = useState<FileList | null>(null);

  const [userLocations, setUserLocations] = useState<LocationType[]>([]);
  const [userTemplates, setUserTemplates] = useState<Template[]>([]);

  const [isFormLoading, setFormLoading] = useState<boolean>(false);
  const [isFormSubmitted, setFormSubmitted] = useState<boolean>(false);

  const documentFields = [
    {
      id: "location_id",
      title: "Филиал",
      type: "select",
      placeholder: "Выберите филиал",
      options: [
        ...userLocations
          .filter((loc) => loc.status)
          .map((loc) => ({ value: String(loc.location_id), label: loc.location })),
      ],
    },
    {
      id: "template_id",
      title: "Шаблон",
      type: "select",
      placeholder: formRecords.location_id ? "Выберите шаблон" : "Сначала выберите филиал",
      options: [...userTemplates.map((temp) => ({ value: String(temp.template_id), label: temp.name }))],
    },
    {
      id: "contragent_id",
      title: "Контрагент",
      type: "select",
      placeholder: "Выберите контрагента",
      options: [
        ...contractors
          .filter((contr) => contr.status)
          .map((contr) => ({ value: String(contr.contragent_id), label: contr.contragent })),
      ],
    },
    {
      id: "lease_number",
      title: "Номер договора",
      type: "text",
      placeholder: "Номер договора",
      options: undefined,
    },
    {
      id: "contract_start_date",
      title: "Дата начала действия договора",
      type: "date",
      placeholder: undefined,
      options: undefined,
    },
    {
      id: "contract_end_date",
      title: "Дата окончания действия договора",
      type: "date",
      placeholder: undefined,
      options: undefined,
    },
    {
      id: "description",
      title: "Информация по договору",
      type: "textarea",
      placeholder: "Впишите краткое описание",
      options: undefined,
    },
    {
      id: "file",
      title: "Загрузить договоры",
      type: "file",
      placeholder: undefined,
      options: undefined,
    },
  ] as const;

  useEffect(() => {
    getLocationsData();
    getBudgetTypesData();
    getContractorsData();
  }, []);

  useEffect(() => {
    if (copyData && "lease_number" in copyData) {
      setFormRecords((prev) => ({
        ...prev,
        location_id: copyData.location_id?.toString() || "",
        template_id: copyData.template_id?.toString() || "",
        contragent_id: copyData.contragent_id?.toString() || "",
        lease_number: copyData.lease_number?.toString() || "",
        contract_start_date: copyData.contract_start_date?.toString() || "",
        contract_end_date: copyData.contract_end_date?.toString() || "",
        description: copyData.description?.toString() || "",
      }));
    }
  }, [copyData, userLocations, userTemplates, contractors]);

  useEffect(() => {
    formRecords.location_id && getTemplatesData();
    formRecords.template_id = "";
  }, [formRecords.location_id]);

  const getLocationsData = async () => {
    try {
      const userLocationsData: LocationType[] = await getLocationsByUser();
      setUserLocations(userLocationsData);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении филиалов");
    }
  };

  const getTemplatesData = async () => {
    try {
      const userTemplates = await getTemplatesByLocation("Договоры", +formRecords.location_id);
      setUserTemplates(userTemplates);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении шаблонов");
    }
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};
    REQUIRED_FIELDS.forEach((field) => {
      if (!formRecords[field]?.trim()) {
        newErrors[field] = true;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const files = (e.target as HTMLInputElement).files;

    files && setFileList(files);

    setFormRecords((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleSelectChange = (option: any, actionMeta: any) => {
    const { name } = actionMeta;
    const value = option?.value || "";

    setFormRecords((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const submitDocument = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      notify("Заполните все поля", "danger");
      return;
    }

    setFormLoading(true);
    try {
      const formData = new FormData();

      const documentForm = {
        location_id: +formRecords.location_id,
        template_id: +formRecords.template_id,
        choosen_location: userLocations.find((item) => item.location_id === +formRecords.location_id)?.location,
        template_name: userTemplates.find((item) => item.template_id === +formRecords.template_id)?.name,
        contragent_id: +formRecords.contragent_id,
        lease_number: formRecords.lease_number,
        contract_start_date: formRecords.contract_start_date,
        contract_end_date: formRecords.contract_end_date,
        description: formRecords.description,
      };

      if (fileList) {
        Array.from(fileList).forEach((file) => {
          formData.append(`files`, file);
        });
      }

      const request_id = await createDocumentRequest(documentForm);
      await uploadDocumentFiles(request_id, formData);
      notify("Договор создан", "success");

      setFormSubmitted(true);
      const filters = new URLSearchParams();
      filters.append("locations", String(locationName));
      getDocumentsData(undefined, filters);
      getDocumentsTableData(undefined, filters);
      onClose?.();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при создании договора");
    } finally {
      setFormLoading(false);
    }
  };

  const renderField = ({ id, title, type, placeholder, options }: (typeof documentFields)[number]) => {
    const Component = type === "textarea" ? "textarea" : "input";
    return (
      <div className={id === "description" || id === "file" ? "col-lg-12" : "col-lg-4"} key={id}>
        <div className="mb-3">
          <label className="form-label required">{title}</label>
          <div className="input-icon mb-3">
            {type === "select" ? (
              <Select
                classNamePrefix="react-select"
                name={id}
                placeholder={placeholder}
                value={options?.find((opt) => opt.value === formRecords[id])}
                onChange={handleSelectChange}
                options={options}
                isDisabled={isFormLoading}
                className={errors[id] ? "is-invalid" : ""}
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            ) : (
              <Component
                type={type}
                className={`form-control ${errors[id] ? "is-invalid" : ""}`}
                name={id}
                max={type === "date" ? "9999-12-31" : undefined}
                placeholder={placeholder}
                value={formRecords[id] || ""}
                onChange={handleInputChange}
                disabled={isFormLoading}
                multiple={type === "file"}
              />
            )}

            {errors[id] && <div className="invalid-feedback">{getInputErrorMessage(id)}</div>}
            {isFormLoading && (
              <span className="input-icon-addon">
                <div className="spinner-border spinner-border-sm text-secondary" role="status" />
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const newDocumentBody = (
    <form id="documentForm" className="row" onSubmit={submitDocument}>
      {documentFields.map(renderField)}
    </form>
  );

  const newDocumentFooter = (
    <div className="d-flex gap-2">
      {onClose && (
        <button type="button" className="btn btn-ghost-secondary" onClick={onClose} disabled={isFormLoading}>
          Отмена
        </button>
      )}
      <button type="submit" form="documentForm" className="btn btn-primary d-flex gap-1" disabled={isFormLoading}>
        Создать договор
        {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
      </button>
    </div>
  );

  useEffect(() => {
    if (isFormSubmitted && onClose) {
      // Close the modal after a brief delay to allow user to see success message
      const timer = setTimeout(() => {
        onClose();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isFormSubmitted, onClose]);

  return (
    <ModalComponent
      header="Новый договор"
      body={newDocumentBody}
      footer={newDocumentFooter}
      disableClose={isFormLoading}
      shouldModalClose={isFormSubmitted}
    />
  );
}
